/** @jsxImportSource @emotion/react */
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import { useANCSchedule } from "./hooks/useANCSchedule";
import ANCScheduleBoard from "./ANCScheduleBoard";
import ANCRescheduleDialog from "./ANCRescheduleDialog";
import { history } from "../../../../Helpers/history";
import {
    SchedulePageWrapper,
    ScheduleHeader,
    HeaderTitle,
    HeaderStats,
    StatBadge,
    EmptyState,
    GenerateBtn,
    LoadingWrapper,
} from "./ANCScheduleStyles";

/**
 * ANCSchedule — Page-level container for ANC Visit Scheduling (8-Contact Model)
 *
 * Provides:
 *  - Visual schedule board with 8 ANC contacts
 *  - Progress tracking, color-coded statuses
 *  - Reschedule dialog
 *  - Navigation to record a contact
 *
 * Route: /gyn-and-obs-anc-schedule/:patient_number/:visit_id/:patient_type
 */
const ANCSchedule = function(props) {
    const actions = props.actions;
    const snackbar = props.snackbar;
    const params = props.match.params;
    const patient_number = params.patient_number;
    const visit_id = params.visit_id;
    const patient_type = params.patient_type;

    /* ─── Current user ─── */
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userObj = !user ? {} : user;
    const token = !userObj.token ? [] : userObj.token;
    const _token = !token[1] ? {} : token[1];
    const doctor_name = (!_token.first_name ? "" : _token.first_name) + " " +
        (!_token.last_name ? "" : _token.last_name);

    /* ─── ANC Schedule hook ─── */
    const ancSchedule = useANCSchedule(actions, patient_number, visit_id);

    /* ─── Snackbar ─── */
    const closeSnackbar = function() { actions.snackbarActions.hideSnackbar(); };
    const openBar = snackbar.openBar;
    const type = snackbar.type;
    const message = snackbar.message;

    /* ─── Navigate to ANC booking ─── */
    const goToBooking = function() {
        history.push(
            "/gyn-and-obs-antenatal-card/" + patient_number + "/" + visit_id + "/" + patient_type
        );
    };

    /* ─── Navigate to clinical assessment to record a contact ─── */
    const goToRecordContact = function(item) {
        history.push(
            "/gyn-and-obs-anc-assessment/" + patient_number + "/" + visit_id + "/" + patient_type
        );
    };

    return (
        <div className="journals">
            <PageTitle title="ANC Visit Schedule" />
            <SubHeader
                title="OB/GYN"
                subTitle="ANC Visit Schedule"
                doctor_name={doctor_name}
            >
                <div className="header-icon-margin-right-zero">
                    <img
                        src="/images/antenatal-blue.png"
                        className="icon-resize surgery-icon-tabs ante-natal-green"
                        alt="ANC"
                    />
                </div>
            </SubHeader>
            <MainSnackbar
                variant={type}
                open={openBar}
                message={message}
                handleCloseBar={closeSnackbar}
            />

            <ObGynSidebar
                patient_number={patient_number}
                visit_id={visit_id}
                patient_type={patient_type}
            >
                <SchedulePageWrapper>
                    {/* ═══ PATIENT INFO ═══ */}
                    <ObGynPatientInfo
                        patient_number={patient_number}
                        visit_id={visit_id}
                    />

                    {/* ═══ LOADING STATE ═══ */}
                    {ancSchedule.loadingBooking && (
                        <LoadingWrapper>
                            <span>⏳</span> Loading patient ANC data...
                        </LoadingWrapper>
                    )}

                    {/* ═══ NO BOOKING STATE ═══ */}
                    {!ancSchedule.loadingBooking && !ancSchedule.bookingData && (
                        <div style={{ marginTop: "16px" }}>
                            <EmptyState>
                                <div className="empty-icon">📋</div>
                                <div className="empty-title">No Active ANC Booking Found</div>
                                <div className="empty-desc">
                                    This patient must be registered in the Antenatal Care program
                                    before a visit schedule can be generated. 
                                </div>
                                <GenerateBtn onClick={goToBooking}>
                                    Go to ANC Registration & Booking
                                </GenerateBtn>
                            </EmptyState>
                        </div>
                    )}

                    {/* ═══ MAIN CONTENT ═══ */}
                    {!ancSchedule.loadingBooking && ancSchedule.bookingData && (
                        <div style={{ marginTop: "16px" }}>
                            {/* ─── Header with stats ─── */}
                            <ScheduleHeader>
                                <HeaderTitle>
                                    <h3>📅 ANC Visit Schedule</h3>
                                    <p>
                                        LNMP: {ancSchedule?.bookingData?.lnmp || "N/A"}
                                        {ancSchedule?.bookingData?.edd ? " · EDD: " + ancSchedule.bookingData.edd : ""}
                                        {ancSchedule?.gestationalAge ? " · GA: " + ancSchedule.gestationalAge.display + " weeks" : ""}
                                    </p>
                                </HeaderTitle>
                                <HeaderStats>
                                    <StatBadge>
                                        ✓ Attended <span className="count">{ancSchedule?.summary?.attended}</span>
                                    </StatBadge>
                                    <StatBadge>
                                        ⏱ Upcoming <span className="count">{ancSchedule?.summary?.upcoming}</span>
                                    </StatBadge>
                                    <StatBadge>
                                        ✕ Missed <span className="count">{ancSchedule?.summary?.missed}</span>
                                    </StatBadge>
                                    <StatBadge>
                                        ○ Future <span className="count">{ancSchedule?.summary?.future}</span>
                                    </StatBadge>
                                </HeaderStats>
                            </ScheduleHeader>

                            {/* ─── Schedule Board ─── */}
                            <ANCScheduleBoard
                                schedule={ancSchedule?.schedule}
                                summary={ancSchedule?.summary}
                                nextContact={ancSchedule?.nextContact}
                                onReschedule={ancSchedule?.openReschedule}
                                onRecordContact={goToRecordContact}
                                loading={ancSchedule.loadingSchedule}
                                gestationalAge={ancSchedule.gestationalAge}
                            />

                            {/* ─── Reschedule Dialog ─── */}
                            <ANCRescheduleDialog
                                open={ancSchedule.rescheduleDialog.open}
                                item={ancSchedule.rescheduleDialog.item}
                                rescheduleDate={ancSchedule.rescheduleDate}
                                onDateChange={ancSchedule.setRescheduleDate}
                                onConfirm={ancSchedule.handleReschedule}
                                onClose={ancSchedule.closeReschedule}
                                saving={ancSchedule.saving}
                            />
                        </div>
                    )}
                </SchedulePageWrapper>
            </ObGynSidebar>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        snackbar: state.snackbar,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            snackbarActions: bindActionCreators(snackbarActions, dispatch),
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ANCSchedule);
